/**
 * Edpies — SubjectThumbnail
 *
 * Renders a real illustrated "poster" image for a subject (Maths, English,
 * Science, Physics, Chemistry, Biology, Verbal/Non-Verbal Reasoning, Creative
 * Writing). Images live in /public/subject-images and are shared across the
 * whole student journey (dashboard, resources, practice, mocks, STEM library)
 * so every subject tile looks consistent with the landing page.
 *
 * Falls back to a soft gradient chip for any subject without a dedicated
 * illustration (e.g. History, Geography).
 */

import { motion } from 'framer-motion';
import { GraduationCap } from 'lucide-react';

const SIZE_CLASSES = {
  sm: 'h-10 w-10',
  md: 'h-14 w-14',
  lg: 'h-16 w-16',
  xl: 'h-20 w-20',
};

// Subjects that have a dedicated illustration in /public/subject-images.
const SUBJECT_IMAGES = new Set([
  'maths', 'english', 'science', 'physics', 'chemistry', 'biology',
  'verbal-reasoning', 'non-verbal-reasoning', 'creative-writing',
]);

/** Normalise any free-form subject label/key to an image key. */
const normaliseSubject = (raw = '') => {
  const s = String(raw).toLowerCase().replace(/_/g, '-').trim();
  if (s.includes('non') && s.includes('verbal')) return 'non-verbal-reasoning';
  if (s.includes('nvr')) return 'non-verbal-reasoning';
  if (s.includes('verbal') || s === 'vr') return 'verbal-reasoning';
  if (s.includes('creative') || s.includes('writing')) return 'creative-writing';
  if (s.includes('math')) return 'maths';
  if (s.includes('physic')) return 'physics';
  if (s.includes('chem')) return 'chemistry';
  if (s.includes('biolog')) return 'biology';
  if (s.includes('eng')) return 'english';
  if (s.includes('science')) return 'science';
  return s;
};

const FALLBACK_BG = 'bg-gradient-to-br from-slate-200 to-slate-300';

/** Renders the correct subject thumbnail for a subject key. */
const SubjectThumbnail = ({ subject = '', size = 'md', animate = true, className = '' }) => {
  const key = normaliseSubject(subject);
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const hasImage = SUBJECT_IMAGES.has(key);

  const inner = hasImage ? (
    <div className={`${sizeClass} rounded-xl overflow-hidden shadow-sm ring-1 ring-black/5 ${className}`}>
      <img
        src={`${process.env.PUBLIC_URL || ''}/subject-images/${key}.png`}
        alt={`${key.replace(/-/g, ' ')} illustration`}
        className="h-full w-full object-cover"
        loading="lazy"
        draggable="false"
      />
    </div>
  ) : (
    <div
      className={`${sizeClass} rounded-xl ${FALLBACK_BG} flex items-center justify-center text-slate-500 ${className}`}
      aria-hidden="true"
    >
      <GraduationCap className="h-1/2 w-1/2" />
    </div>
  );

  if (!animate) return inner;
  return (
    <motion.div
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      className="inline-flex"
    >
      {inner}
    </motion.div>
  );
};

export default SubjectThumbnail;
